import { AssayResult, NormalisationMode, type StrippedFullAbundance } from './types';

export const NormalisationModesAndProperties = {
  [NormalisationMode.MG_SS]: 'copiesPerMgSS',
  [NormalisationMode.HOUR]: 'copiesPerHour',
  [NormalisationMode.LITRE]: 'copiesPerL',
  [NormalisationMode.TEN_UL_DILUTED_DNA]: 'absolute',
  [NormalisationMode.MG_BOD]: 'copiesPerMgBod',
  [NormalisationMode.SIXTEEN_S]: 'relative',
} as const; // satisfies Partial<Record<NormalisationMode, keyof FullAbundance>>;

export function isConsideredAbsolute(normalisationMode: NormalisationMode) {
  return normalisationMode !== NormalisationMode.SIXTEEN_S;
}

export function getNormalisedValue(
  abundanceOrResult: StrippedFullAbundance | Pick<AssayResult, 'value' | 'normalisationMode'>,
  normalisationMode: keyof typeof NormalisationModesAndProperties,
) {
  if ('value' in abundanceOrResult && 'normalisationMode' in abundanceOrResult) {
    if (abundanceOrResult.normalisationMode === normalisationMode) {
      return abundanceOrResult.value;
    } else {
      throw Error(`Normalisation mode mismatch: ${abundanceOrResult.normalisationMode} !== ${normalisationMode}`);
    }
  }
  const modeProperty = NormalisationModesAndProperties[normalisationMode];
  return abundanceOrResult[modeProperty];
}

export const NormalisedValueName = {
  [NormalisationMode.SIXTEEN_S]: 'Relative abundance',
  [NormalisationMode.MG_SS]: 'Copy numbers per mg of SS',
  [NormalisationMode.HOUR]: 'Copy numbers per hour',
  [NormalisationMode.LITRE]: 'Copy numbers per L',
  [NormalisationMode.TEN_UL_DILUTED_DNA]: 'Copies in 10 µL DNA',
  [NormalisationMode.MG_BOD]: 'Copy numbers per mg BOD',
} as const;

export function getNormalisationDescription(mode: NormalisationMode): { description: string; valueTip: string } {
  switch (mode) {
    case NormalisationMode.MG_SS:
      return {
        description: 'Copies / mg of SS (log)',
        valueTip: 'The graph presents Log₁₀ of gene copies per mg of suspended solids',
      };
    case NormalisationMode.HOUR:
      return {
        description: 'Copies per hour (log)',
        valueTip: 'The graph presents Log₁₀ of copies per hour (flow normalised)',
      };
    case NormalisationMode.LITRE:
      return {
        description: 'Copies per litre (log)',
        valueTip: 'The graph presents Log₁₀ of gene copies per litre',
      };
    case NormalisationMode.MG_BOD:
      return {
        description: 'Copies / mg BOD (log)',
        valueTip: 'The graph presents Log₁₀ of gene copies per mg of biochemical oxygen demand',
      };
    case NormalisationMode.TEN_UL_DILUTED_DNA:
      return {
        description: 'Copies in 10 µL DNA (log)',
        valueTip:
          'The graph presents Log₁₀ of the numbers of gene copies in 10 µL of diluted DNA analysed on the SmartChip qPCR run.',
      };
    case NormalisationMode.SIXTEEN_S:
      return {
        description: 'Relative abundance (log)',
        valueTip: 'The graph presents Log₁₀ of abundance, relative to 16S rRNA genes',
      };
  }
}

// Display normalization mode labels based on screen size
export function getShortNormalisationLabel(mode: NormalisationMode, isWideScreen: boolean = true): string {
  switch (mode) {
    case NormalisationMode.SIXTEEN_S:
      return isWideScreen ? 'Relative abundances' : 'Rel. abund.';
    case NormalisationMode.MG_SS:
      return isWideScreen ? 'Copy num. per mg SS' : 'Per mg SS';
    case NormalisationMode.HOUR:
      return isWideScreen ? 'Copy num. per hour' : 'Per hour';
    case NormalisationMode.LITRE:
      return isWideScreen ? 'Copy num. per L' : 'Per L';
    case NormalisationMode.TEN_UL_DILUTED_DNA:
      return isWideScreen ? 'Copies in 10 µL DNA' : 'Copy #';
    case NormalisationMode.MG_BOD:
      return isWideScreen ? 'Copy num. per mg BOD' : 'Per mg BOD';
    default:
      return 'Unknown';
  }
}
